/**
 * Hooks de Supabase Realtime Presence para tracking de visitantes en vivo.
 *
 * - useTrackPresence(canal): marca al usuario actual como presente en un canal.
 * - useWatchPresence(canal): solo escucha, no se marca a sí mismo. Devuelve el conteo.
 *
 * Canales convencionales:
 *   - 'presence:landing'  → visitantes de limpack.uy
 *   - 'presence:tienda'   → visitantes de tienda.limpack.uy
 *   - 'presence:cliente'  → clientes logueados en panel.limpack.uy/cliente
 */
import { useEffect, useState, useRef } from 'react'
import { supabase } from './supabase'

// ID anónimo persistente por tab (limpia al cerrar la pestaña)
function getTabId() {
  if (typeof window === 'undefined') return 'ssr'
  let id = sessionStorage.getItem('limpack_tab_id')
  if (!id) {
    id = 'anon_' + Math.random().toString(36).slice(2, 10)
    sessionStorage.setItem('limpack_tab_id', id)
  }
  return id
}

/**
 * Marca al usuario actual como presente en un canal.
 * @param {string} canal - ej 'presence:tienda'
 * @param {object} meta - info extra (user_id si está logueado, etc)
 */
export function useTrackPresence(canal, meta = {}) {
  const channelRef = useRef(null)

  useEffect(() => {
    if (!canal) return
    const tabId = getTabId()

    // Cleanup defensivo: si el canal ya existe (StrictMode doble-mount, navegación, etc.)
    // removerlo antes de crear el nuevo para evitar "cannot add callbacks after subscribe()"
    try {
      const existing = supabase.getChannels().filter(c => c.topic === `realtime:${canal}`)
      existing.forEach(c => supabase.removeChannel(c))
    } catch {}

    const channel = supabase.channel(canal, {
      config: { presence: { key: tabId } }
    })
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        try {
          await channel.track({
            tab_id: tabId,
            online_at: new Date().toISOString(),
            ...meta,
          })
        } catch (e) { console.warn('presence track:', e?.message || e) }
      }
    })
    channelRef.current = channel
    return () => {
      try { channel.untrack() } catch {}
      try { supabase.removeChannel(channel) } catch {}
      channelRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canal])
}

/**
 * Observa la cantidad de presentes en uno o varios canales.
 * @param {string[]} canales - ej ['presence:landing', 'presence:tienda']
 * @returns {{ [canal]: number }} conteo por canal
 */
export function useWatchPresence(canales) {
  const [counts, setCounts] = useState({})

  useEffect(() => {
    if (!canales || canales.length === 0) return
    const channels = canales.map(canal => {
      // Cleanup defensivo: si el canal ya existe (StrictMode doble-mount, navegación rápida)
      // hay que removerlo antes de hacer .on, sino tira:
      // "cannot add `presence` callbacks for realtime:<canal> after subscribe()"
      try {
        const existing = supabase.getChannels().filter(c => c.topic === `realtime:${canal}`)
        existing.forEach(c => supabase.removeChannel(c))
      } catch {}

      const ch = supabase.channel(canal)
      ch.on('presence', { event: 'sync' }, () => {
        const state = ch.presenceState() || {}
        setCounts(prev => ({ ...prev, [canal]: Object.keys(state).length }))
      })
      ch.subscribe()
      return ch
    })
    return () => {
      channels.forEach(ch => { try { supabase.removeChannel(ch) } catch {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(canales)])

  return counts
}
